import React, { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useHistory } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowForward from '@mui/icons-material/ArrowForward';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, postData, deleteData, activateData } from '../../../apiService';
import Link from 'react-router-dom/Link';
import Info from '@mui/icons-material/Info';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import CopyrightIcon from '@mui/icons-material/Copyright';
import AddIcon from '@mui/icons-material/Add';
import { Tooltip } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import PrintIcon from '@mui/icons-material/Print';
import { Box } from '@mui/material';
import InvoicePrint from './Print';
import ReactDOM from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import JoinRightIcon from '@mui/icons-material/JoinRight';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DownloadDoneIcon from '@mui/icons-material/DownloadDone';
import { generateInvoiceQuotationPDF } from "./generateQuotationPDF";
import { generateInvoiceExcel } from "./generateInvoiceExcel";
import DescriptionIcon from '@mui/icons-material/Description';

function InvoiceIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const history = useHistory(); // Access the navigation history
    const [loading, setLoading] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const { isAuthenticated, companyId, branchId, userId, permissions, token, logout } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Master');
    const hasEditMasterPermission = permissions.includes('Edit Master');
    const hasDeleteMasterPermission = permissions.includes('Delete Master');

    useEffect(() => {
        // // Check if the token is expired
        if (isAuthenticated && tokenIsExpired(token)) {
            // Token is expired, log out the user
            logout();
            return;
        }
        // Token is not expired, fetch data
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        fetchTableData();
    }, [isAuthenticated, companyId, branchId]);

    // Auto-refresh data when fromDate or toDate changes
    useEffect(() => {
        if (fromDate && toDate && isAuthenticated) {
            fetchTableData();
        }
    }, [fromDate, toDate]);

    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);

    //search
    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    // Function to fetch table data and set state
    const fetchTableData = async () => {
        setData([]);
        setLoading(true);
        fetchData('InvoiceApi', `/${companyId}/${branchId}/${fromDate}/${toDate}`, token)//, token
            .then((response) => {
                if (response.responseCode == '1111') {
                    console.log(response);
                    setData(response.response_data);
                }
            })
            .catch((error) => {
                console.error('Error fetching Invoice List:', error);
            })
            .finally(() => {
                setLoading(false); // Hide loading screen
            });
    };

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        setFromDate(selectedDate);
    };

    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        setToDate(selectedDate);
    };

    const handleDelete = async () => {
        deleteData('InvoiceApi', `${deleteItemId}`, token)//, token
            .then((response) => {
                fetchTableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const handleActivate = (itemId) => {
        activateData('InvoiceApi', `${itemId}`)//, token
            .then(() => {
                fetchTableData();
            })
            .catch((error) => {
                console.error('Error activating record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    function formatDate(dateString) {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        const formattedDate = new Date(dateString).toLocaleDateString('en-GB', options);
        return formattedDate;
    }

    const handlePrint = async (InvId) => {
        try {
            const response = await fetchData('InvoiceApi', `/InvoicePrint/${InvId}`, token); //, token
            const labelData = response.response_data;
            const labelsToPrint = Array.isArray(labelData) ? labelData : [labelData];

            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            document.body.appendChild(iframe);

            const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
            iframeDocument.write('<html><head><style>@media print { header, footer { display: none !important; } }</style></head><body>');

            ReactDOM.render(
                <InvoicePrint labelData={labelData} onClose={() => document.body.removeChild(iframe)} />,
                iframeDocument.body
            );

            iframeDocument.write('</body></html>');
            iframeDocument.close();

            setTimeout(() => {
                iframe.contentWindow.print();
            }, 1000);

        } catch (error) {
            console.error('Error printing QA print :', error);
        }
    };

    const [isModalOpen, setIsModalOpen] = useState(false);

    // Invoice Details 
    const [InvoiceData, setInvoiceData] = useState([]);
    const [InvoiceDetails, setInvoiceDetails] = useState([]);

    const fetchInvoiceDetailsData = async (id) => {
        setInvoiceDetails([]);
        setLoading(true);
        fetchData('InvoiceApi', `/InvoiceDetails/${id}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    console.log(response);
                    setInvoiceData(response.response_data.Invoice);
                    setInvoiceDetails(response.response_data.InvoiceDetails);
                    setIsModalOpen(true);  // ✅ explicitly open, not toggle
                }
            })
            .catch((error) => {
                console.error('Error fetching Invoice Details:', error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleGenerate = async (QaId) => {
        try {
            // Fetch invoice data
            const response = await fetchData('InvoiceApi', `/InvoicePrint/${QaId}`, token);
            const labelData = response.response_data;

            // Fetch company data
            let companyData = null;
            try {
                const companyResponse = await fetchData('CompanyApi', `/${companyId}`, token);
                if (companyResponse.responseCode === '1111' && companyResponse.response_data) {
                    companyData = companyResponse.response_data;
                }
            } catch (error) {
                console.error('Error fetching company data:', error);
                // Continue with default company info if API fails
            }

            if (labelData) {
                generateInvoiceQuotationPDF(labelData, companyData);
            } else {
                console.error("No quotation data received.");
            }
        } catch (error) {
            console.error("Error generating quotation PDF:", error);
        }
    };

    const handleGenerateExcel = async (InvId) => {
        try {
            // Fetch invoice data
            const response = await fetchData('InvoiceApi', `/InvoicePrint/${InvId}`, token);
            const labelData = response.response_data;

            // Fetch company data
            let companyData = null;
            try {
                const companyResponse = await fetchData('CompanyApi', `/${companyId}`, token);
                if (companyResponse.responseCode === '1111' && companyResponse.response_data) {
                    companyData = companyResponse.response_data;
                }
            } catch (error) {
                console.error('Error fetching company data:', error);
                // Continue with default company info if API fails
            }

            if (labelData) {
                generateInvoiceExcel(labelData, companyData);
            } else {
                console.error("No invoice data received.");
            }
        } catch (error) {
            console.error("Error generating invoice Excel:", error);
        }
    };

    return (
        <div className="container mt-2 mx-auto" style={{ maxWidth: '100%' }}>
            {loading && (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(0,0,0,0.5)',
                        zIndex: 50,
                    }}
                >
                    {/* Inline Diamond Loader */}
                    <div
                        style={{
                            position: 'relative',
                            width: '50px',
                            height: '50px',
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            transform: 'rotate(45deg)',
                            overflow: 'hidden',
                            borderRadius: '6px',
                            boxShadow: '0 6px 18px rgba(255,255,255,0.2)',
                        }}
                    >
                        <div
                            style={{
                                position: 'absolute',
                                inset: '6px',
                                margin: 'auto',
                                background: '#ffffff',
                                borderRadius: '4px',
                            }}
                        ></div>
                        <div
                            style={{
                                position: 'absolute',
                                inset: '-12px',
                                margin: 'auto',
                                background: '#ff66cc',
                                borderRadius: '6px',
                                animation: 'diamondLoader 2s linear infinite',
                            }}
                        ></div>
                        <style>
                            {`
            @keyframes diamondLoader {
              0%,10% { transform: translate(-50px,-50px) rotate(-45deg); }
              90%,100% { transform: translate(0px,0px) rotate(-45deg); }
            }
          `}
                        </style>
                    </div>
                </div>
            )}

            {/* Filter Bar */}
            <div
                className="flex items-center font-serif gap-3 p-2 rounded-xl shadow-sm"
                style={{ background: "#f9fafb", marginBottom: "10px", marginTop: '-20px' }}
            >
                <h6
                    className="uppercase tracking-wide font-serif text-black mt-2 text-gray-700 flex-grow"
                    style={{ fontSize: "14px", fontWeight: "500" }}
                >
                    Invoice
                </h6>

                {/* Search */}
                <input
                    type="text"
                    placeholder="🔍 Search..."
                    value={searchQuery}
                    onChange={handleSearch}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    style={{
                        width: isFocused ? "240px" : "180px",
                        height: "30px",
                        border: "1px solid #ddd",
                        borderRadius: "20px",
                        padding: "0 14px",
                        fontSize: "14px",
                        transition: "all 0.3s ease",
                        outline: "none",
                        fontFamily: 'serif'
                    }}
                />

                {/* From Date */}
                <input
                    type="date"
                    value={fromDate}
                    onChange={handleFromDateChange}
                    style={{
                        height: "30px",
                        fontSize: "12px",
                        border: "1px solid #ddd",
                        borderRadius: "6px",
                        padding: "0 6px",
                        fontFamily: 'Arial, sans-serif',
                    }}
                />

                {/* To Date */}
                <input
                    type="date"
                    value={toDate}
                    onChange={handleToDateChange}
                    style={{
                        height: "30px",
                        fontSize: "12px",
                        border: "1px solid #ddd",
                        borderRadius: "6px",
                        padding: "0 6px",
                        fontFamily: 'Arial, sans-serif',
                    }}
                />

                {/* Refresh Button */}
                <button
                    onClick={fetchTableData}
                    style={{
                        background: "#1090a3",
                        color: "white",
                        fontSize: "12px",
                        border: "none",
                        borderRadius: "20px",
                        padding: "4px 14px",
                        cursor: "pointer",
                        transition: "0.2s",
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.background = "#0b6d7a")}
                    onMouseOut={(e) => (e.currentTarget.style.background = "#1090a3")}
                >
                    Refresh
                </button>
                {/* {hasCreateMasterPermission && (
                    <Tooltip title="Create New Quotation" arrow>
                        <Button
                            component={Link}
                            to="/QuotationCreate"
                            style={{
                                background: "#3F4F44",
                                color: "white",
                                fontSize: "12px",
                                border: "none",
                                borderRadius: "20px",
                                padding: "4px 14px",
                                cursor: "pointer",
                                transition: "0.2s",
                                fontFamily: 'inherit'
                            }}
                        >
                            Create
                        </Button>
                    </Tooltip>
                )} */}
            </div>

            <div className="watermark" >
                <CopyrightIcon /><b style={{ fontFamily: 'serif' }}>EFFTABS SOLUTIONS LLP</b>
            </div>

            <div className='mb-2 flex' style={{ maxHeight: '520px', overflowY: 'auto' }}>
                <TableContainer component={Paper}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    style={{
                                        backgroundColor: "#0f172a", borderRadius: '5px', color: 'white', width: '2%',
                                        fontWeight: '600', fontSize: '10px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    Sl.No
                                </TableCell>
                                <TableCell
                                    style={{
                                        backgroundColor: "#0f172a", borderRadius: '5px', color: 'white', width: '3%',
                                        fontWeight: '600', fontSize: '10px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    Bill Date
                                </TableCell>
                                <TableCell
                                    style={{
                                        backgroundColor: "#0f172a", borderRadius: '5px', color: 'white', width: '10%',
                                        fontWeight: '600', fontSize: '10px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    Bill No
                                </TableCell>
                                <TableCell
                                    style={{
                                        backgroundColor: "#0f172a", borderRadius: '5px', color: 'white', width: '3%',
                                        fontWeight: '600', fontSize: '10px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    QA No
                                </TableCell>
                                <TableCell
                                    style={{
                                        backgroundColor: "#0f172a", borderRadius: '5px', color: 'white', width: '15%',
                                        fontWeight: '600', fontSize: '10px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    QA_Rev_No
                                </TableCell>
                                <TableCell
                                    style={{
                                        backgroundColor: "#0f172a", borderRadius: '5px', color: 'white', width: '35%',
                                        fontWeight: '600', fontSize: '10px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    Name
                                </TableCell>
                                <TableCell
                                    style={{
                                        backgroundColor: "#0f172a", borderRadius: '5px', color: 'white', width: '5%',
                                        fontWeight: '600', fontSize: '10px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    GST No
                                </TableCell>
                                <TableCell
                                    style={{
                                        backgroundColor: "#0f172a", borderRadius: '5px', color: 'white', width: '10%',
                                        fontWeight: '600', fontSize: '10px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    Taxable(₹)
                                </TableCell>
                                <TableCell
                                    style={{
                                        backgroundColor: "#0f172a", borderRadius: '5px', color: 'white', width: '5%',
                                        fontWeight: '600', fontSize: '10px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    GST(₹)
                                </TableCell>

                                <TableCell
                                    style={{
                                        backgroundColor: "#0f172a", borderRadius: '5px', color: 'white', width: '10%',
                                        fontWeight: '600', fontSize: '10px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', textTransform: 'uppercase'
                                    }}>
                                    Total(₹)
                                </TableCell>
                                <TableCell
                                    style={{
                                        backgroundColor: "#0f172a", borderRadius: '5px', color: 'white', width: '10%',
                                        fontWeight: '600', fontSize: '10px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                        top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '3px', borderRadius: '4px', width: '10%', textTransform: 'uppercase'
                                    }}>
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {filteredData && filteredData.map((item, index) => (
                                <TableRow
                                    key={item.Id}
                                    sx={{
                                        '&:hover': { backgroundColor: '#F1E7E7' },
                                        transition: 'background-color 0.3s ease-in-out',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <TableCell sx={{ padding: '2px', border: '1px solid #f8d7da', width: '2%', fontSize: '12px' }}>{index + 1}</TableCell>
                                    <TableCell sx={{ padding: '2px', border: '1px solid #f8d7da', textAlign: 'center', width: '3%', fontSize: '12px' }}>
                                        {item.InvoiceDate ? formatDate(item.InvoiceDate) : "-"}
                                    </TableCell>
                                    <TableCell
                                        sx={{ padding: '2px', border: '1px solid #f8d7da', width: '10%', fontSize: '12px', cursor: 'pointer', color: 'red', fontWeight: 'bolder' }}
                                        onClick={() => fetchInvoiceDetailsData(item.Id)}   // ✅ this will open modal + load data
                                    >
                                       {item.InvoiceNo}
                                    </TableCell>
                                    <TableCell sx={{ padding: '2px', border: '1px solid #f8d7da', width: '10%', fontSize: '11px' }}>{item.QuotationNumber}</TableCell>
                                    <TableCell sx={{ padding: '2px', border: '1px solid #f8d7da', width: '15%', fontSize: '11px' }}>{item.RevisionNumber ? item.RevisionNumber : "-"}</TableCell>
                                    <TableCell sx={{ padding: '2px', border: '1px solid #f8d7da', width: '35%', fontSize: '12px' }}>{item.CustomerName}</TableCell>
                                    <TableCell sx={{ padding: '2px', border: '1px solid #f8d7da', width: '10%', fontSize: '12px' }}>{item.GSTNumber ? item.GSTNumber : "-"}</TableCell>
                                    <TableCell sx={{ padding: '2px', textAlign: 'right', border: '1px solid #f8d7da', width: '10%', fontSize: '12px' }}>
                                        {item.TaxableAmount ? item.TaxableAmount.toFixed(2) : "0.00"}
                                    </TableCell>
                                    <TableCell sx={{ padding: '2px', textAlign: 'right', border: '1px solid #f8d7da', fontSize: '12px' }}>
                                        {(
                                            (item.CGSTAmount || 0) +
                                            (item.SGSTAmount || 0) +
                                            (item.IGSTAmount || 0)
                                        ).toFixed(2)}
                                    </TableCell>
                                    <TableCell sx={{ padding: '2px', textAlign: 'right', border: '1px solid #f8d7da', width: '10%', fontSize: '12px' }}>
                                        {item.TotalAmount ? item.TotalAmount.toFixed(2) : "0.00"}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            padding: '2px',
                                            border: '1px solid #f8d7da',
                                            width: '10%',
                                            fontSize: '12px'
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-end',
                                                gap: '4px',
                                                flexWrap: 'nowrap',
                                            }}
                                        >
                                            {/* <Tooltip title="Invoice Print" arrow>
                                                <IconButton size="small" onClick={() => handlePrint(item.Id)}>
                                                    <PrintIcon sx={{ fontSize: 18, color: 'darkmagenta' }} />
                                                </IconButton>
                                            </Tooltip> */}

                                            <Tooltip title="Invoice Print" arrow>
                                                <IconButton size="small" onClick={() => handleGenerate(item.Id)}>
                                                    <PrintIcon sx={{ fontSize: 18, color: 'darkmagenta' }} />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Export to Excel" arrow>
                                                <IconButton size="small" onClick={() => handleGenerateExcel(item.Id)}>
                                                    <DescriptionIcon sx={{ fontSize: 18, color: 'green' }} />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Invoice Details" arrow>
                                                <IconButton
                                                    size="small"
                                                    component={Link}
                                                    to={`/InvoiceDetails/${item.Id}`}
                                                >
                                                    <Info sx={{ fontSize: 20, color: 'coral' }} />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title={item.IsActive ? "Cancel Invoice" : "Activate Invoice"} arrow>
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id)
                                                    }
                                                    color="error"
                                                >
                                                    <DeleteIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>

                                </TableRow>
                            ))}
                        </TableBody>

                        <Dialog
                            open={confirmDialogOpen}
                            onClose={closeConfirmDialog}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                        >
                            <DialogTitle id="alert-dialog-title">{"Cancel This Invoice?"}</DialogTitle>
                            <DialogContent>
                                <DialogContentText id="alert-dialog-description">
                                    Are you sure you want to Cancel this Invoice?
                                </DialogContentText>
                            </DialogContent>

                            <DialogActions>
                                <button onClick={closeConfirmDialog} className="bg-red-600 text-white border border-red-600 rounded px-4 py-2 mr-2">
                                    Cancel
                                </button>
                                <button onClick={handleDelete} className="bg-green-600 text-white border border-green-600 rounded px-4 py-2">
                                    Confirm
                                </button>
                            </DialogActions>
                        </Dialog>
                    </Table>
                    {!loading && data.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '50px', color: '#5d2929ff', fontSize: '14px' }}>
                            No Data Available
                        </div>
                    )}

                    {!loading && data.length > 0 && (
                        <div>
                            {/* Render your table or content here */}
                        </div>
                    )}
                </TableContainer>
            </div>

            {isModalOpen && (
                <div className='mb-2 flex' style={{ maxHeight: '520px', overflowY: 'auto' }}>
                    <div className="fixed inset-0 flex items-center justify-center z-10">
                        <div className="bg-white p-6 rounded-lg shadow-lg relative" style={{ width: '95%' }}>
                            <Tooltip title="Close Invoice Details">
                                <IconButton
                                    onClick={() => setIsModalOpen(false)}
                                    sx={{ position: 'absolute', top: 10, right: 10, zIndex: 20 }}
                                    size="small"
                                    aria-label="close"
                                    style={{ cursor: 'pointer' }}
                                >
                                    <CloseIcon fontSize="small" sx={{ color: 'darkred', cursor: 'pointer' }} />
                                </IconButton>
                            </Tooltip>

                            <h6 style={{ fontWeight: 'bold', textTransform: 'uppercase', color: '#4C063C', textAlign: 'center', fontSize: '13px' }}>Invoice Details</h6>
                            {InvoiceData && Object.keys(InvoiceData).length > 0 ? (
                                <div className="p-3 rounded-lg shadow-md bg-white border border-gray-200">
                                    <div className="grid grid-cols-1 md:grid-cols-5 gap-1 text-sm">
                                        <p style={{ fontSize: '11.5px' }}>
                                            <span className="font-semibold">QA No: </span>
                                            {InvoiceData.QuotationNumber}
                                        </p>
                                        {InvoiceData.RevisionNumber != null && (
                                            <p style={{ fontSize: '11.5px' }}>
                                                <span className="font-semibold">Rev No: </span>
                                                {InvoiceData.RevisionNumber}
                                            </p>
                                        )}
                                        <p style={{ fontSize: '11.5px' }}>
                                            <span className="font-semibold">Customer: </span>
                                            {InvoiceData.CustomerName}
                                        </p>
                                        <p style={{ fontSize: '11.5px' }}>
                                            <span className="font-semibold">GST No: </span>
                                            {InvoiceData.GSTNumber}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-center text-gray-500 text-sm py-4">
                                    Loading...
                                </p>
                            )}

                            <TableContainer component={Paper} style={{ height: '450px' }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                style={{
                                                    background: 'linear-gradient(to right, #111110ff, #111110ff)', borderRadius: '5px', color: 'white',
                                                    fontWeight: '600', fontSize: '10px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                                    top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '2px', borderRadius: '4px', textTransform: 'uppercase', width: '2%'
                                                }}>
                                                Sl.No
                                            </TableCell>
                                            <TableCell
                                                style={{
                                                    background: 'linear-gradient(to right, #111110ff, #111110ff)', borderRadius: '5px', color: 'white',
                                                    fontWeight: '600', fontSize: '10px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                                    top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '2px', borderRadius: '4px', width: '7%', textTransform: 'uppercase'
                                                }}>
                                                Item
                                            </TableCell>
                                            <TableCell
                                                style={{
                                                    background: 'linear-gradient(to right, #111110ff, #111110ff)', borderRadius: '5px', color: 'white',
                                                    fontWeight: '600', fontSize: '10px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                                    top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '2px', borderRadius: '4px', width: '7%', textTransform: 'uppercase'
                                                }}>
                                                Name
                                            </TableCell>
                                            <TableCell
                                                style={{
                                                    background: 'linear-gradient(to right, #111110ff, #111110ff)', borderRadius: '5px', color: 'white',
                                                    fontWeight: '600', fontSize: '10px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                                    top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '2px', borderRadius: '4px', width: '30%', textTransform: 'uppercase'
                                                }}>
                                                Details
                                            </TableCell>
                                            <TableCell
                                                style={{
                                                    background: 'linear-gradient(to right, #111110ff, #111110ff)', borderRadius: '5px', color: 'white',
                                                    fontWeight: '600', fontSize: '10px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                                    top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '2px', borderRadius: '4px', width: '3%', textTransform: 'uppercase'
                                                }}>
                                                UOM
                                            </TableCell>
                                            <TableCell
                                                style={{
                                                    background: 'linear-gradient(to right, #111110ff, #111110ff)', borderRadius: '5px', color: 'white',
                                                    fontWeight: '600', fontSize: '10px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                                    top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '2px', borderRadius: '4px', width: '3%', textTransform: 'uppercase'
                                                }}>
                                                QTY
                                            </TableCell>
                                            <TableCell
                                                style={{
                                                    background: 'linear-gradient(to right, #111110ff, #111110ff)', borderRadius: '5px', color: 'white',
                                                    fontWeight: '600', fontSize: '10px', border: '1px solid #ccc', textAlign: 'center', position: 'sticky',
                                                    top: 0, zIndex: 10, whiteSpace: 'nowrap', padding: '2px', borderRadius: '4px', width: '7%', textTransform: 'uppercase'
                                                }}>
                                                Total
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Array.isArray(InvoiceDetails) &&
                                            Object.entries(
                                                InvoiceDetails.reduce((groups, item) => {
                                                    const work = item.WorkName || "Others";
                                                    if (!groups[work]) groups[work] = [];
                                                    groups[work].push(item);
                                                    return groups;
                                                }, {})
                                            ).map(([workName, items], groupIndex) => (
                                                <React.Fragment key={groupIndex}>
                                                    {/* Heading Row for WorkName */}
                                                    <TableRow sx={{ backgroundColor: '#f3e8ff', height: '1%' }}>
                                                        <TableCell
                                                            colSpan={8}
                                                            sx={{
                                                                fontWeight: 'bold',
                                                                fontSize: '11.5px',
                                                                color: '#4C063C',
                                                                border: '1px solid #d1c4e9',
                                                                textTransform: 'uppercase',
                                                                padding: '3px'
                                                            }}
                                                        >
                                                            {workName}
                                                        </TableCell>
                                                    </TableRow>

                                                    {/* Child rows for each item under this WorkName */}
                                                    {items.map((item, index) => (
                                                        <TableRow
                                                            key={item.Id}
                                                            sx={{
                                                                '&:hover': { backgroundColor: '#F1E7E7' },
                                                                transition: 'background-color 0.3s ease-in-out'
                                                            }}
                                                        >
                                                            <TableCell sx={{ padding: '2px', border: '1px solid #f8d7da', width: '2%', fontSize: '12px' }}>
                                                                {index + 1}
                                                            </TableCell>
                                                            <TableCell sx={{ padding: '2px', border: '1px solid #f8d7da', width: '7%', fontSize: '12px' }}>
                                                                {item.WorkdItemName}
                                                            </TableCell>
                                                            <TableCell sx={{ padding: '2px', border: '1px solid #f8d7da', width: '7%', fontSize: '12px' }}>
                                                                {item.DetailsName}
                                                            </TableCell>
                                                            <TableCell sx={{ padding: '2px', border: '1px solid #f8d7da', width: '30%', fontSize: '12px' }}>
                                                                {item.WorkdDetailsName}
                                                            </TableCell>
                                                            <TableCell sx={{ padding: '2px', border: '1px solid #f8d7da', width: '3%', fontSize: '12px', textAlign: 'center' }}>
                                                                {item.UOM}
                                                            </TableCell>
                                                            <TableCell sx={{ padding: '2px', border: '1px solid #f8d7da', width: '3%', fontSize: '12px', textAlign: 'right' }}>
                                                                {item.Quantity}
                                                            </TableCell>
                                                            <TableCell sx={{ padding: '2px', border: '1px solid #f8d7da', width: '7%', fontSize: '12px', textAlign: 'right' }}>
                                                                {item.TotalAmount}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </React.Fragment>
                                            ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>
                    </div>
                </div>
            )}

        </div >
    );
}

export default InvoiceIndex;

